import React, { useState, useEffect } from "react";
import Dashboard from "./Dashboard";
import "./AccountsDashboard.css";

function AccountsDashboard() {
  const [bills, setBills] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [editingBill, setEditingBill] = useState(null);
  const [showBillForm, setShowBillForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    quarter_id: "",
    bill_type: "electricity",
    period: new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    amount: "",
    status: "pending"
  });
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [filter, setFilter] = useState({
    quarter_no: "",
    bill_type: "",
    status: ""
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Load data on component mount
  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([fetchQuarters(), fetchBills()]);
    } catch (err) {
      console.error('Error initializing data:', err);
      setError('Failed to load data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuarters = async () => {
    try {
      console.log('Fetching quarters...');
      const response = await fetch(`${API_BASE_URL}/api/quarters`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch quarters: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Quarters fetched:', data);
      setQuarters(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching quarters:', err);
      throw err;
    }
  };

  const fetchBills = async () => {
    try {
      console.log('Fetching bills...');
      const response = await fetch(`${API_BASE_URL}/api/bills/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('No bills endpoint found, starting with empty bills');
          setBills([]);
          return;
        }
        throw new Error(`Failed to fetch bills: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Bills fetched:', data);
      setBills(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching bills:', err);
      // Don't throw here, just log the error and continue with empty bills
      setBills([]);
    }
  };

  // Enhanced bill validation
  const validateBill = (billData) => {
    const errors = [];
    
    if (!billData.quarter_id) errors.push('Quarter is required');
    if (!billData.amount || parseFloat(billData.amount) <= 0) errors.push('Valid amount is required');
    if (!billData.period) errors.push('Period is required');
    if (!billData.bill_type) errors.push('Bill type is required');
    if (!billData.status) errors.push('Status is required');
    
    return errors;
  };

  const handleAddBill = async () => {
    const errors = validateBill(formData);
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      console.log('Adding bill:', formData);
      
      const response = await fetch(`${API_BASE_URL}/api/bills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        throw new Error(`Failed to add bill: ${response.status} - ${errorText}`);
      }
      
      const newBill = await response.json();
      console.log('Bill added successfully:', newBill);
      
      // Update local state
      setBills(prevBills => [...prevBills, newBill]);
      
      // Reset form and close
      setShowBillForm(false);
      resetForm();
      
      alert('Bill added successfully!');
      
      // Refresh data from server to ensure consistency
      await fetchBills();
      
    } catch (err) {
      console.error('Error adding bill:', err);
      alert(`Failed to add bill: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditBill = (bill) => {
    setEditingBill(bill);
    setFormData({
      quarter_id: bill.quarter_id?.toString() || "",
      bill_type: bill.bill_type || "electricity",
      period: bill.period || "",
      amount: bill.amount?.toString() || "",
      status: bill.status || "pending"
    });
    setShowBillForm(true);
  };

  const handleUpdateBill = async () => {
    const errors = validateBill(formData);
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      console.log('Updating bill:', editingBill.id, formData);
      
      const response = await fetch(`${API_BASE_URL}/api/bills/${editingBill.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          updated_at: new Date().toISOString()
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        throw new Error(`Failed to update bill: ${response.status} - ${errorText}`);
      }
      
      const updatedBill = await response.json();
      console.log('Bill updated successfully:', updatedBill);
      
      // Update local state
      setBills(prevBills => 
        prevBills.map(bill => 
          bill.id === updatedBill.id ? updatedBill : bill
        )
      );
      
      // Reset form and close
      setEditingBill(null);
      setShowBillForm(false);
      resetForm();
      
      alert('Bill updated successfully!');
      
      // Refresh data from server to ensure consistency
      await fetchBills();
      
    } catch (err) {
      console.error('Error updating bill:', err);
      alert(`Failed to update bill: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBill = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bill? This action cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    try {
      console.log('Deleting bill:', id);
      
      const response = await fetch(`${API_BASE_URL}/api/bills/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        throw new Error(`Failed to delete bill: ${response.status} - ${errorText}`);
      }
      
      console.log('Bill deleted successfully');
      
      // Update local state
      setBills(prevBills => prevBills.filter(bill => bill.id !== id));
      
      alert('Bill deleted successfully!');
      
    } catch (err) {
      console.error('Error deleting bill:', err);
      alert(`Failed to delete bill: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const clearBills = async () => {
    if (!window.confirm('Are you sure you want to delete ALL bills? This action cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    try {
      console.log('Clearing all bills...');
      
      const response = await fetch('${API_BASE_URL}/api/bills/all', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        throw new Error(`Failed to clear bills: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('All bills cleared successfully:', result);
      
      // Update local state
      setBills([]);
      alert(`All bills cleared successfully! ${result.message}`);
      
    } catch (err) {
      console.error('Error clearing bills:', err);
      alert(`Failed to clear bills: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      quarter_id: "",
      bill_type: "electricity",
      period: new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      amount: "",
      status: "pending"
    });
  };
 
    // Helper function to get resident name - using 'name' field from database
  const getResidentName = (quarter) => {
    if (!quarter) return 'Unknown';
    return quarter.name && quarter.name.trim() !== '' ? quarter.name : 'Vacant';
  };

  // Enhanced filtering
  const filteredBills = bills.filter(bill => {
    if (!bill) return false;
    
    const quarter = quarters.find(q => q && q.id === bill.quarter_id);
    
    if (filter.quarter_no && quarter?.quarter_no && 
        !quarter.quarter_no.toLowerCase().includes(filter.quarter_no.toLowerCase())) {
      return false;
    }
    if (filter.bill_type && bill.bill_type !== filter.bill_type) {
      return false;
    }
    if (filter.status && bill.status !== filter.status) {
      return false;
    }
    return true;
  });

  // Calculate totals with error handling
  const totalAmount = bills.reduce((sum, bill) => {
    const amount = parseFloat(bill?.amount || 0);
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);
  
  const paidAmount = bills
    .filter(bill => bill?.status === "paid")
    .reduce((sum, bill) => {
      const amount = parseFloat(bill?.amount || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    
  const pendingAmount = bills
    .filter(bill => bill?.status === "pending")
    .reduce((sum, bill) => {
      const amount = parseFloat(bill?.amount || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

  // Enhanced loading state
  if (loading) {
    return (
      <div className="accounts-dashboard-container">
        <div className="container">
          <div className="loading-spinner">
            <p>Loading accounts data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced error state
  if (error) {
    return (
      <div className="accounts-dashboard-container">
        <div className="container">
          <div className="error-message">
            <h2>⚠️ Error Loading Data</h2>
            <p>{error}</p>
            <div className="form-buttons">
              <button className="btn-primary" onClick={initializeData}>
                🔄 Retry
              </button>
              <button className="btn-secondary" onClick={() => window.location.reload()}>
                🔄 Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accounts-dashboard-container">
      <div className="container">
        <div className="dashboard-header">
          <h1>💰 Accounts Dashboard</h1>
          {saving && (
            <div className="saving-indicator">
              <span>💾 Saving...</span>
            </div>
          )}
        </div>
        
        <p className="dashboard-subtitle">
          Manage electricity and maintenance bills for all quarters. Data is automatically saved to the database.
        </p>

        {/* Connection Status */}
        <div className="card">
          <div className="connection-status">
            <p>🔗 Connected to database | {bills.length} bills loaded | Last updated: {new Date().toLocaleTimeString()}</p>
            <button className="btn-secondary" onClick={() => fetchBills()}>
              🔄 Refresh Data
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>Total Bills</h3>
              <span className="stat-number">{bills.length}</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <h3>Total Amount</h3>
              <span className="stat-number">₹{totalAmount.toLocaleString()}</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <h3>Paid Amount</h3>
              <span className="stat-number">₹{paidAmount.toLocaleString()}</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <h3>Pending Amount</h3>
              <span className="stat-number">₹{pendingAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="card">
          <h2>🔍 Filter Bills</h2>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Quarter Number</label>
              <input
                type="text"
                placeholder="e.g., A-1, B-2"
                value={filter.quarter_no}
                onChange={(e) => setFilter({...filter, quarter_no: e.target.value})}
              />
            </div>
            
            <div className="filter-group">
              <label>Bill Type</label>
              <select
                value={filter.bill_type}
                onChange={(e) => setFilter({...filter, bill_type: e.target.value})}
              >
                <option value="">All Types</option>
                <option value="electricity">Electricity</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Status</label>
              <select
                value={filter.status}
                onChange={(e) => setFilter({...filter, status: e.target.value})}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            
            <button 
              className="btn-secondary"
              onClick={() => setFilter({quarter_no: "", bill_type: "", status: ""})}
            >
              Clear Filters
            </button>
          </div>
        </div>

        
        {/* Add/Edit Bill Form */}
        {showBillForm && (
          <div className="card bill-form">
            <h2>{editingBill ? "Edit Bill" : "Add New Bill"}</h2>
            
            <div className="form-group">
              <label>Search by PNO Number or Resident Name</label>
              <input
                type="text"
                placeholder="Search by PNO or resident name..."
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setSearchQuery(query);
                  
                  if (query.trim() === "") {
                    setSearchResults([]);
                  } else {
                    const results = quarters.filter(q => 
                      (q.pno_no && q.pno_no.toString().toLowerCase().includes(query.toLowerCase())) ||
                      (q.name && q.name.toLowerCase().includes(query.toLowerCase()))
                    );
                    setSearchResults(results);
                  }
                }}
              />
              
              {searchResults.length > 0 && (
                <div className="search-results-dropdown">
                  {searchResults.map(quarter => (
                    <div
                      key={quarter.id}
                      className="search-result-item"
                      onClick={() => {
                        setFormData({...formData, quarter_id: quarter.id.toString()});
                        setSearchQuery(`${quarter.pno_no} - ${getResidentName(quarter)} (${quarter.quarter_no})`);
                        setSearchResults([]);
                      }}
                    >
                      <div className="result-info">
                        <strong>PNO: {quarter.pno_no}</strong>
                        <span className="result-resident">{getResidentName(quarter)}</span>
                        <span className="result-quarter">{quarter.quarter_no} - {quarter.block}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {searchQuery && searchResults.length === 0 && (
                <div className="search-no-results">No quarters found</div>
              )}
            </div>
            
            <div className="form-group">
              <label>Quarter *</label>
              <select
                value={formData.quarter_id}
                onChange={(e) => setFormData({...formData, quarter_id: e.target.value})}
                required
              >
                <option value="">Select Quarter</option>
                {quarters.map(quarter => (
                  <option key={quarter.id} value={quarter.id}>
                    {quarter.quarter_no} - {quarter.block} - {quarter.resident || 'Vacant'}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Bill Type *</label>
                <select
                  value={formData.bill_type}
                  onChange={(e) => setFormData({...formData, bill_type: e.target.value})}
                  required
                >
                  <option value="electricity">⚡ Electricity Bill</option>
                  <option value="maintenance">🔧 Maintenance Bill</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Period *</label>
                <input
                  type="text"
                  value={formData.period}
                  onChange={(e) => setFormData({...formData, period: e.target.value})}
                  placeholder="e.g., Sep 2025"
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Amount (₹) *</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="Enter amount"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  required
                >
                  <option value="pending">⏳ Pending</option>
                  <option value="paid">✅ Paid</option>
                </select>
              </div>
            </div>
            
            <div className="form-buttons">
              <button 
                className="btn-primary"
                onClick={editingBill ? handleUpdateBill : handleAddBill}
                disabled={saving || !formData.quarter_id || !formData.amount || !formData.period}
              >
                {saving ? "💾 Saving..." : (editingBill ? "✏️ Update Bill" : "➕ Add Bill")}
              </button>
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowBillForm(false);
                  setEditingBill(null);
                  resetForm();
                }}
                disabled={saving}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        )}

        {/* Bills Management Section */}
        <div className="card">
          <div className="card-header">
            <h2>📋 Bills Management</h2>
            <div className="header-actions">
              {bills.length > 0 && (
                <button 
                  className="btn-clear"
                  onClick={clearBills}
                  disabled={saving}
                >
                  🗑️ Clear All Bills
                </button>
              )}
              <button 
                className="btn-primary"
                onClick={() => setShowBillForm(true)}
                disabled={saving}
              >
                ➕ Add New Bill
              </button>
            </div>
          </div>

          {filteredBills.length > 0 ? (
            <div className="table-container">
              <table className="bills-table">
                <thead>
                  <tr>
                    <th>Bill ID</th>
                    <th>Quarter No.</th>
                    <th>Resident</th>
                    <th>Type</th>
                    <th>Period</th>
                    <th>Amount (₹)</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBills.map((bill) => {
                    const quarter = quarters.find(q => q && q.id === bill.quarter_id);
                    return (
                      <tr key={bill.id}>
                        <td>#{bill.id}</td>
                        <td>{quarter?.quarter_no || "N/A"}</td>
                        <td>{quarter?.resident || "Vacant"}</td>
                        <td className="bill-type">
                          {bill.bill_type === 'electricity' ? '⚡ Electricity' : '🔧 Maintenance'}
                        </td>
                        <td>{bill.period}</td>
                        <td>₹{parseFloat(bill.amount || 0).toLocaleString()}</td>
                        <td>
                          <span className={`status-badge ${bill.status}`}>
                            {bill.status === 'paid' ? '✅' : '⏳'} {bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-edit"
                              onClick={() => handleEditBill(bill)}
                              disabled={saving}
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => handleDeleteBill(bill.id)}
                              disabled={saving}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-bills">
              <p>
                {bills.length === 0 
                  ? "📝 No bills added yet. Click 'Add New Bill' to get started." 
                  : "🔍 No bills match your current filters."}
              </p>
            </div>
          )}
        </div>

        {/* Bill Type Summary */}
        <div className="card">
          <h2>📊 Bill Type Summary</h2>
          <div className="summary-grid">
            <div className="summary-item">
              <h3>⚡ Electricity Bills</h3>
              <p>Total: ₹{bills.filter(b => b.bill_type === "electricity").reduce((sum, b) => sum + parseFloat(b.amount || 0), 0).toLocaleString()}</p>
              <p>Count: {bills.filter(b => b.bill_type === "electricity").length}</p>
            </div>
            <div className="summary-item">
              <h3>🔧 Maintenance Bills</h3>
              <p>Total: ₹{bills.filter(b => b.bill_type === "maintenance").reduce((sum, b) => sum + parseFloat(b.amount || 0), 0).toLocaleString()}</p>
              <p>Count: {bills.filter(b => b.bill_type === "maintenance").length}</p>
            </div>
            <div className="summary-item">
              <h3>✅ Paid Bills</h3>
              <p>Total: ₹{paidAmount.toLocaleString()}</p>
              <p>Count: {bills.filter(b => b.status === "paid").length}</p>
            </div>
            <div className="summary-item">
              <h3>⏳ Pending Bills</h3>
              <p>Total: ₹{pendingAmount.toLocaleString()}</p>
              <p>Count: {bills.filter(b => b.status === "pending").length}</p>
            </div>
          </div>
        </div>

        {/* Main Dashboard at the end */}
        <div className="main-dashboard-section">
          <h2 className="section-title">Main Dashboard Overview</h2>
          <Dashboard />
        </div>
      </div>
    </div>
  );
}

export default AccountsDashboard;